import Vue from 'vue';

import api from '@/api';
import * as ArcGIS from '@/helpers/ArcGIS';
import { EMERGENCIA_CLASE, RECURSO_SECTOR_ESTADO_TIPO, RECURSO_SECTOR_ESTADO_TIPO_ID } from '@/helpers/constants';
import DateService, { FMT } from '@/helpers/DateService';
import { actualizarEmergencia, normalizeGraficos } from '@/helpers/EmergenciaHelper';
import i18n from '../../plugins/i18n.plugin';
import { comparadorString } from '@/helpers/FunctionHelper';
import { sameId } from '@/helpers/IdHelper';
import {
  defineSyncSchema,
  createSyncState,
  createSyncRequest,
  recordWrite,
  isStaleResponse,
  collectProtected,
  mergeRows,
  schemaKinds,
  syncMutations,
} from '@/helpers/storeSync';

const EMERGENCIA_SYNC_SCHEMA = defineSyncSchema({
  key: 'ID_EMERGENCIA',
  kind: 'emergencia',
  insertAt: 'end',
  overlays: [{ kind: 'incendio', fields: ['INCENDIO'] }],
  children: {
    SECTORES: {
      key: 'ID_SECTOR',
      kind: 'sector',
      insertAt: 'end',
      children: {
        RECURSOS: {
          key: 'ID_RECURSO_SECTOR',
          kind: 'recursoSector',
          insertAt: 'end',
          overlays: [{ kind: 'recursoSectorGuardia', fields: ['MEDIO_PERSONAL', 'ID_MEDIO_PERSONAL'] }],
        },
      },
    },
    PLANES_OPERACIONES: { key: 'ID_PLAN_OPERACIONES', kind: 'plan', insertAt: 'start' },
    PERIMETRO: { key: 'ID_PERIMETRO', kind: 'perimetro', insertAt: 'start' },
    NIVELES: { key: 'ID_EMERGENCIA_NIVEL', kind: 'nivel', insertAt: 'start' },
    ESTADOS: { key: 'ID_EMERGENCIA_ESTADO', kind: 'estado', insertAt: 'start' },
    DIRECTOR_EXTINCION: { key: 'ID_DIRECTOR_EXTINCION', kind: 'director', insertAt: 'end' },
  },
});

const initialState = {
  // Emergencias activas
  emergencias: [],

  emergenciasTipo: [],

  emergenciasCategoria: [],

  emergenciasClase: [],

  emergenciaEstadoTipo: [],

  tiposEmergenciaNivel: [],

  sync: createSyncState(schemaKinds(EMERGENCIA_SYNC_SCHEMA)),

  corruptGraficosNotified: false,
};

const getters = {
  emergencias: (state) => state.emergencias,

  emergenciasTipo: (state) => state.emergenciasTipo,

  emergenciaEstadoTipo: (state) => state.emergenciaEstadoTipo,

  emergenciasFiltradas: (state) => {
    const emergencias = state.emergencias
      .filter((e) => !e.OCULTO && !e.ELIMINADO)
      .sort((a, b) => {
        const aFecha = DateService.toDayjs(a.FECHA_CREACION);
        const bFecha = DateService.toDayjs(b.FECHA_CREACION);

        if (aFecha.isAfter(bFecha)) return -1;
        if (aFecha.isBefore(bFecha)) return 1;
        return 0;
      });
    return emergencias;
  },

  emergenciasCategoria: (state) => state.emergenciasCategoria,

  emergenciasClase: (state) => state.emergenciasClase,

  getEmergenciaByID: (state) => (idEmergencia) => {
    let emergencia = null;

    if (idEmergencia) {
      emergencia = state.emergencias.find((x) => x.ID_EMERGENCIA.equalsIgnoreCase(idEmergencia));
    }
    return emergencia;
  },

  getEmergenciasFiltradasByID: (state) => (idEmergencia) => {
    let emergencia = null;

    if (idEmergencia) {
      emergencia = state.emergenciasFiltradas.find((x) => x.ID_EMERGENCIA.equalsIgnoreCase(idEmergencia));
    }
    return emergencia;
  },

  getEmergenciaNombre: () => (emergencia) => {
    return emergencia?.NOMBRE || emergencia?.MUNICIPIO?.NOMBRE || i18n.t('emergencies.unknown');
  },

  getEmergenciaByIncendioID: (state) => (idIncendio) => {
    let emergencia = null;

    if (idIncendio) {
      emergencia = state.emergencias.find((x) => x.INCENDIO?.ID_INCENDIO.equalsIgnoreCase(idIncendio));
    }
    return emergencia;
  },

  getEmergenciaEstadoTipoByValue: (state, getters) => (idEmergenciaTipo, value) => {
    const estados = getters.getEstadosByTipo(idEmergenciaTipo);
    return estados.find((e) => e.VALUE === value) || null;
  },

  getEstadosByTipo: (state) => (idEmergenciaTipo) => {
    if (!state.emergenciaEstadoTipo || state.emergenciaEstadoTipo.length === 0) {
      return [];
    }

    // Obtener la categoría y clase a partir del tipo
    const emergenciaTipo = state.emergenciasTipo.find((t) => t.ID_EMERGENCIA_TIPO === idEmergenciaTipo);
    if (!emergenciaTipo) {
      console.warn(i18n.t('alert.emergencyTypeNotFound'));
      return [];
    }

    const idEmergenciaCategoria = emergenciaTipo.ID_EMERGENCIA_CATEGORIA;
    const emergenciaCategoria = state.emergenciasCategoria.find(
      (c) => c.ID_EMERGENCIA_CATEGORIA === idEmergenciaCategoria
    );
    const idEmergenciaClase = emergenciaCategoria ? emergenciaCategoria.ID_EMERGENCIA_CLASE : null;

    let estadosFiltrados = state.emergenciaEstadoTipo.filter((x) =>
      x.EMERGENCIA_ESTADO_TIPO_ASIGNACIONES.some((a) => a.ID_EMERGENCIA_TIPO === idEmergenciaTipo)
    );

    if (estadosFiltrados.length > 0) {
      return estadosFiltrados.sort((x, y) => x.VALUE - y.VALUE);
    }

    estadosFiltrados = state.emergenciaEstadoTipo.filter((x) =>
      x.EMERGENCIA_ESTADO_TIPO_ASIGNACIONES.some((a) => a.ID_EMERGENCIA_CATEGORIA === idEmergenciaCategoria)
    );

    if (estadosFiltrados.length > 0) {
      return estadosFiltrados.sort((x, y) => x.VALUE - y.VALUE);
    }

    estadosFiltrados = state.emergenciaEstadoTipo.filter((x) =>
      x.EMERGENCIA_ESTADO_TIPO_ASIGNACIONES.some((a) => a.ID_EMERGENCIA_CLASE === idEmergenciaClase)
    );

    if (estadosFiltrados.length > 0) {
      return estadosFiltrados.sort((x, y) => x.VALUE - y.VALUE);
    }

    estadosFiltrados = state.emergenciaEstadoTipo.filter((x) =>
      x.EMERGENCIA_ESTADO_TIPO_ASIGNACIONES.every(
        (a) => !a.ID_EMERGENCIA_TIPO && !a.ID_EMERGENCIA_CATEGORIA && !a.ID_EMERGENCIA_CLASE
      )
    );

    return estadosFiltrados.sort((x, y) => x.VALUE - y.VALUE);
  },

  getEstadoTipoById: (state) => (idEstadoTipo) => {
    let emergenciaEstadoTipo = null;

    if (idEstadoTipo) {
      emergenciaEstadoTipo = state.tiposEmergenciaEstado.find((x) => x.ID_EMERGENCIA_ESTADO_TIPO === idEstadoTipo);
    }
    return emergenciaEstadoTipo;
  },

  getRecursoSectorByEmergenciaId: (state) => (idEmergencia, idRecurso) => {
    if (!idEmergencia || !idRecurso) return [];

    const emergencia = state.emergencias.find((e) => e.ID_EMERGENCIA === idEmergencia);
    if (!emergencia?.SECTORES?.length) return [];

    const recursoSector = emergencia.SECTORES[0].RECURSOS.find((r) => r.ID_RECURSO === idRecurso);
    return recursoSector || [];
  },

  getRecursosOrdenadosByEmergencia: (state, getters, rootState, rootGetters) => (emergencia) => {
    if (!emergencia?.SECTORES?.length) return [];

    const getRecursoNameById = rootGetters['recurso/getRecursoNameById'];
    return (emergencia.SECTORES ?? [])
      .flatMap((s) => s?.RECURSOS ?? [])
      .sort((a, b) => {
        if (a.ID_RECURSO === b.ID_RECURSO) {
          const estadoA = a.RECURSO_SECTOR_ESTADO.find(
            (estado) => estado.ID_RECURSO_SECTOR_ESTADO_TIPO === RECURSO_SECTOR_ESTADO_TIPO_ID.ASIGNADO
          );
          const estadoB = b.RECURSO_SECTOR_ESTADO.find(
            (estado) => estado.ID_RECURSO_SECTOR_ESTADO_TIPO === RECURSO_SECTOR_ESTADO_TIPO_ID.ASIGNADO
          );

          const fechaA = DateService.toDayjs(estadoA.FECHA);
          const fechaB = DateService.toDayjs(estadoB.FECHA);

          return fechaA.valueOf() - fechaB.valueOf();
        }
        const nombreA = getRecursoNameById(a.ID_RECURSO);
        const nombreB = getRecursoNameById(b.ID_RECURSO);
        return nombreA.localeCompare(nombreB, 'es', { sensitivity: 'base' });
      });
  },

  getDirectoresPosiblesByEmergenciaId: (state, getters, rootState, rootGetters) => (idEmergencia) => {
    if (!idEmergencia) return [];
    const emergencia = state.emergencias.find((e) => e.ID_EMERGENCIA === idEmergencia);

    if (!emergencia || !emergencia.SECTORES?.length) return [];

    const getRecursoNameById = rootGetters['recurso/getRecursoNameById'];

    return (emergencia.SECTORES ?? [])
      .flatMap((s) => s?.RECURSOS ?? [])
      .sort((a, b) => {
        const nombreA = getRecursoNameById(a.ID_RECURSO);
        const nombreB = getRecursoNameById(b.ID_RECURSO);
        return nombreA.localeCompare(nombreB, 'es', { sensitivity: 'base' });
      });
  },

  getDirectoresExtincionByEmergenciaId: (state) => (idEmergencia) => {
    if (!idEmergencia) return [];
    const emergencia = state.emergencias.find((e) => e.ID_EMERGENCIA === idEmergencia);
    return (
      emergencia?.DIRECTOR_EXTINCION?.filter((director) => !director.BORRADO).sort((a, b) => {
        const fechaA = DateService.toDayjs(a.FECHA_INICIO);
        const fechaB = DateService.toDayjs(b.FECHA_INICIO);
        return fechaA.valueOf() - fechaB.valueOf();
      }) || []
    );
  },

  getSectoresByEmergenciaId: (state) => (idEmergencia) => {
    if (!idEmergencia) return [];
    const emergencia = state.emergencias.find((e) => e.ID_EMERGENCIA === idEmergencia);
    return emergencia?.SECTORES || [];
  },

  isCategoriaIncendio: (state) => (idCategoria) => {
    const categoria = state.emergenciasCategoria.find((categoria) => categoria.ID_EMERGENCIA_CATEGORIA === idCategoria);
    if (categoria && categoria.ID_EMERGENCIA_CLASE === EMERGENCIA_CLASE.INCENDIO) return true;
    return false;
  },

  getHistoricoDespachoMedios: (state) => async (emergenciaData, recursos, recursoEstadoTipo) => {
    try {
      const response = await api.emergencia.getHistoricoDespachoMedios(
        emergenciaData.ID_EMERGENCIA,
        recursos,
        recursoEstadoTipo
      );
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const fecha = DateService.toDayjs(emergenciaData.FECHA_DECLARACION).format(FMT.fileName);
      const nombreClase = emergenciaData.IS_INCENDIO ? 'Incendio' : 'Evento';
      link.setAttribute(
        'download',
        `${fecha}_${nombreClase}_${emergenciaData.NOMBRE}_${emergenciaData.NOMBRE_MUNICIPIO}_historico_despacho.xlsx`
      );
      document.body.appendChild(link);
      link.click();
    } catch (error) {
      console.error(i18n.t('alert.error.rscDispatchHistRetrieval'), error);
      throw error;
    }
  },

  tiposEmergenciaNivel: (state) => state.tiposEmergenciaNivel,

  getNivelesByEmergenciaId: (state) => (idEmergencia) => {
    if (!idEmergencia) return [];
    const emergencia = state.emergencias.find((x) => x.ID_EMERGENCIA.equalsIgnoreCase(idEmergencia));
    return emergencia?.NIVELES?.filter((nivel) => !nivel.ELIMINADO) || [];
  },
};

const mutations = {
  ...syncMutations,

  SOCKET_updateEmergencia(state, data) {
    const idx = state.emergencias.findIndex((e) => e.ID_EMERGENCIA.equalsIgnoreCase(data.ID_EMERGENCIA));
    if (idx === -1) return;

    const updated = actualizarEmergencia(state.emergencias[idx], data);
    Vue.set(state.emergencias, idx, updated);

    recordWrite(state.sync, 'emergencia', data.ID_EMERGENCIA);
    if (data.INCENDIO) {
      recordWrite(state.sync, 'incendio', data.ID_EMERGENCIA);
    }

    this.notify(
      i18n.t('alert.emergencyLocation', { nombre: updated.NOMBRE, municipio: updated.MUNICIPIO?.NOMBRE }),
      i18n.t('alert.dataUpdated')
    );
  },

  SOCKET_addGravedadPotencialIncendio(state, data) {
    const owningEmergencia = state.emergencias.find(
      (e) => e.INCENDIO && sameId(e.INCENDIO.ID_INCENDIO, data.ID_INCENDIO)
    );

    if (owningEmergencia) {
      owningEmergencia.INCENDIO.GRAVEDAD_POTENCIAL.unshift(data);
      recordWrite(state.sync, 'incendio', owningEmergencia.ID_EMERGENCIA);
    }
  },

  SOCKET_alineacionesCampbell(state, data) {
    const owningEmergencia = state.emergencias.find(
      (e) => e.INCENDIO && sameId(e.INCENDIO.ID_INCENDIO, data.ID_INCENDIO)
    );
    if (owningEmergencia) {
      owningEmergencia.INCENDIO.ALINEACIONES_CAMPBELL.unshift(data);
      recordWrite(state.sync, 'incendio', owningEmergencia.ID_EMERGENCIA);
    } else {
      return console.warn(owningEmergencia.ID_INCENDIO);
    }

    const emergencia = this.getters['emergencia/getEmergenciaByIncendioID'](owningEmergencia.INCENDIO.ID_INCENDIO);
    if (emergencia) {
      this.notify(
        this.$t('alert.campbellAlignmentModified'),
        this.$t('alert.campbellAlignmentModifiedIn', { municipio: emergencia.MUNICIPIO?.NOMBRE })
      );
    } else {
      console.warn(owningEmergencia.INCENDIO.ID_INCENDIO);
    }
  },

  MERGE_EMERGENCIAS(state, { rows, request }) {
    if (isStaleResponse(state.sync, request)) return;

    let discardedGraficos = 0;

    for (let i = 0; i < rows.length; i++) {
      const emergencia = rows[i];

      if (emergencia.PLANES_OPERACIONES) {
        for (let j = 0; j < emergencia.PLANES_OPERACIONES.length; j++) {
          const plan = emergencia.PLANES_OPERACIONES[j];

          if (plan.GRAFICOS) {
            discardedGraficos += normalizeGraficos(plan.GRAFICOS, plan.ID_PLAN_OPERACIONES);
          }
        }
      }
      if (emergencia.PERIMETRO) {
        for (let j = 0; j < emergencia.PERIMETRO.length; j++) {
          const perimetro = emergencia.PERIMETRO[j];

          if (perimetro.GRAFICOS_PERIMETRO) {
            for (let z = 0; z < perimetro.GRAFICOS_PERIMETRO.length; z++) {
              const oldGrafico = perimetro.GRAFICOS_PERIMETRO[z];

              try {
                rows[i].PERIMETRO[j].GRAFICOS_PERIMETRO[z].geometry = oldGrafico.GEOMETRIA
                  ? JSON.parse(oldGrafico.GEOMETRIA)
                  : oldGrafico.coordenadas;
                rows[i].PERIMETRO[j].GRAFICOS_PERIMETRO[z].symbol = oldGrafico.SIMBOLO
                  ? JSON.parse(oldGrafico.SIMBOLO)
                  : oldGrafico.symbol;
                rows[i].PERIMETRO[j].GRAFICOS_PERIMETRO[z].attributes = oldGrafico.ATRIBUTOS
                  ? JSON.parse(oldGrafico.ATRIBUTOS)
                  : oldGrafico.attributes;
                rows[i].PERIMETRO[j].GRAFICOS_PERIMETRO[z].type = oldGrafico.TIPO ? oldGrafico.TIPO : oldGrafico.type;
              } catch (error) {
                delete rows[i].PERIMETRO[j].GRAFICOS_PERIMETRO[z];
                this.notify(
                  i18n.t('alert.error.error'),
                  i18n.t('alert.error.loadingPerimeters'),
                  i18n.t('alert.error.error')
                );
              }
            }
          }
        }
      }
    }

    if (discardedGraficos > 0 && !state.corruptGraficosNotified) {
      state.corruptGraficosNotified = true;
      this.notify(
        i18n.t('alert.info'),
        i18n.t('alert.operationsPlanGraphicsDiscarded', { count: discardedGraficos }),
        'warn'
      );
    }

    const protectedByKind = collectProtected(state.sync.stamps, request);
    const merged = mergeRows(state.emergencias, rows, EMERGENCIA_SYNC_SCHEMA, protectedByKind);

    merged.forEach((emergencia) => {
      emergencia.SECTORES.sort((a, b) => {
        return a.SECTOR === 'GENERAL' ? -1 : b.SECTOR === 'GENERAL' ? 1 : a.SECTOR.localeCompare(b.SECTOR);
      });
    });

    state.emergencias = merged;
    state.sync.appliedSeq = request.seq;
  },

  CLEAR_EMERGENCIAS(state) {
    state.emergencias = [];
  },

  SET_EMERGENCIA_BY_ID(state, emergencia) {
    const index = state.emergencias.findIndex((x) => x.ID_EMERGENCIA === emergencia.ID_EMERGENCIA);
    if (index !== -1) return;
    state.emergencias.push(emergencia);
  },

  SET_TIPOS_EMERGENCIA_NIVEL(state, tiposEmergenciaNivel) {
    state.tiposEmergenciaNivel = tiposEmergenciaNivel;
  },

  SET_EMERGENCIAS_TIPO(state, claseCategoriaTipos) {
    const clases = [];
    const categorias = [];
    const tipos = [];

    claseCategoriaTipos.forEach((clase) => {
      const { ID_EMERGENCIA_CLASE, EMERGENCIA_CLASE, CATEGORIA } = clase;

      const categoriasProcesadas = (CATEGORIA || []).map((cat) => {
        const { ID_EMERGENCIA_CATEGORIA, EMERGENCIA_CATEGORIA, ID_EMERGENCIA_CLASE, TIPO } = cat;

        const tiposProcesados = (TIPO || []).map((tipo) => {
          const tipoFinal = {
            ID_EMERGENCIA_TIPO: tipo.ID_EMERGENCIA_TIPO,
            EMERGENCIA_TIPO: tipo.EMERGENCIA_TIPO,
            ID_EMERGENCIA_CATEGORIA: tipo.ID_EMERGENCIA_CATEGORIA,
            CATEGORIA: { EMERGENCIA_CATEGORIA },
          };

          tipos.push(tipoFinal);
          return tipoFinal;
        });

        const categoriaFinal = {
          ID_EMERGENCIA_CATEGORIA,
          EMERGENCIA_CATEGORIA,
          ID_EMERGENCIA_CLASE,
          TIPO: tiposProcesados,
        };

        categorias.push(categoriaFinal);
        return categoriaFinal;
      });

      clases.push({
        ID_EMERGENCIA_CLASE,
        EMERGENCIA_CLASE,
        CATEGORIA: categoriasProcesadas,
      });
    });

    state.emergenciasClase = clases.sort((a, b) => comparadorString.compare(a.EMERGENCIA_CLASE, b.EMERGENCIA_CLASE));

    state.emergenciasCategoria = categorias.sort((a, b) =>
      comparadorString.compare(a.EMERGENCIA_CATEGORIA, b.EMERGENCIA_CATEGORIA)
    );

    state.emergenciasTipo = tipos.sort((a, b) => comparadorString.compare(a.EMERGENCIA_TIPO, b.EMERGENCIA_TIPO));
  },

  SOCKET_addNivelEmergencia(state, data) {
    const emergencia = state.emergencias.find((x) => x.ID_EMERGENCIA.equalsIgnoreCase(data.ID_EMERGENCIA));
    if (!emergencia) return;

    // del back solo recibimos el id del tipo de nivel, necesitamos el objeto completo
    const tipoEmergenciaNivel = state.tiposEmergenciaNivel.find((x) =>
      x.ID_TIPO_EMERGENCIA_NIVEL.equalsIgnoreCase(data.ID_TIPO_EMERGENCIA_NIVEL)
    );
    if (!tipoEmergenciaNivel) return;
    data.TIPO_EMERGENCIA_NIVEL = tipoEmergenciaNivel;

    emergencia.NIVELES.unshift(data);
    recordWrite(state.sync, 'nivel', data.ID_EMERGENCIA_NIVEL);
    this.notify(i18n.t('alert.emergencyLevelUpdate', { nivel: data.TIPO_EMERGENCIA_NIVEL.EMERGENCIA_NIVEL }));
  },

  SOCKET_editNivelEmergencia(state, data) {
    const emergencia = state.emergencias.find((x) => x.ID_EMERGENCIA.equalsIgnoreCase(data.ID_EMERGENCIA));

    if (emergencia) {
      const index = emergencia.NIVELES.findIndex((x) =>
        x.ID_EMERGENCIA_NIVEL.equalsIgnoreCase(data.ID_EMERGENCIA_NIVEL)
      );

      if (index !== -1) {
        emergencia.NIVELES.splice(index, 1, {
          ...emergencia.NIVELES[index],
          FECHA_INICIO: data.FECHA_INICIO,
          COMENTARIO: data.COMENTARIO,
          ELIMINADO: data.ELIMINADO,
        });

        recordWrite(state.sync, 'nivel', data.ID_EMERGENCIA_NIVEL);
        this.notify(i18n.t('alert.emergencyLevelUpdated'));
      }
    }
  },

  SET_EMERGENCIAS_ESTADO_TIPO(state, tipos) {
    state.emergenciaEstadoTipo = tipos;
  },

  SOCKET_addEmergencia(state, data) {
    state.emergencias.push(data);
    recordWrite(state.sync, 'emergencia', data.ID_EMERGENCIA);

    this.notify(i18n.t('alert.newEmergency'), i18n.t('alert.emergencyStart', { municipio: data.MUNICIPIO.NOMBRE }));
  },

  SOCKET_addEstadoEmergencia(state, data) {
    const emergencia = state.emergencias.find((x) => x.ID_EMERGENCIA.equalsIgnoreCase(data.ID_EMERGENCIA));

    if (emergencia) {
      data.EMERGENCIA_ESTADO_TIPO = state.emergenciaEstadoTipo.find(
        (x) => x.ID_EMERGENCIA_ESTADO_TIPO === data.ID_EMERGENCIA_ESTADO_TIPO
      );

      emergencia.ESTADOS.unshift(data);
      recordWrite(state.sync, 'estado', data.ID_EMERGENCIA_ESTADO);

      this.notify(
        i18n.t('alert.emergencyStateUpdate', {
          nombre: emergencia.MUNICIPIO?.NOMBRE,
          estado: data.EMERGENCIA_ESTADO_TIPO.EMERGENCIA_ESTADO,
        })
      );
    }
  },
  SOCKET_deleteEstadoEmergencia(state, data) {
    recordWrite(state.sync, 'estado', data.ID_EMERGENCIA_ESTADO, true);

    const emergencia = state.emergencias.find((x) => x.ID_EMERGENCIA.equalsIgnoreCase(data.ID_EMERGENCIA));

    if (emergencia) {
      const index = emergencia.ESTADOS.findIndex((x) => x.ID_EMERGENCIA_ESTADO === data.ID_EMERGENCIA_ESTADO);

      if (index !== -1) {
        emergencia.ESTADOS.splice(index, 1);
        this.notify(
          i18n.t('alert.emergencyStateUpdate', {
            municipio: emergencia.MUNICIPIO?.NOMBRE,
            estado: emergencia.ESTADOS[0].EMERGENCIA_ESTADO_TIPO.EMERGENCIA_ESTADO,
          })
        );
      }
    }
  },

  SOCKET_ocultarEmergencia(state, data) {
    const { OCULTO, EMERGENCIA } = data;
    if (OCULTO) {
      const index = state.emergencias.findIndex((x) => x.ID_EMERGENCIA.equalsIgnoreCase(EMERGENCIA.ID_EMERGENCIA));
      if (index !== -1) {
        this.notify(i18n.t('alert.emergencyHide', { municipio: state.emergencias[index].MUNICIPIO?.NOMBRE }));
        state.emergencias.splice(index, 1, EMERGENCIA);
        recordWrite(state.sync, 'emergencia', EMERGENCIA.ID_EMERGENCIA);
      }
    } else {
      const index = state.emergencias.findIndex((x) => x.ID_EMERGENCIA.equalsIgnoreCase(EMERGENCIA.ID_EMERGENCIA));

      if (index !== -1) {
        state.emergencias.splice(index, 1, EMERGENCIA);
      } else {
        state.emergencias.push(EMERGENCIA);
      }
      recordWrite(state.sync, 'emergencia', EMERGENCIA.ID_EMERGENCIA);

      this.notify(
        i18n.t('alert.addEmergency'),
        i18n.t('alert.emergencyStart', {
          municipio: EMERGENCIA.MUNICIPIO.NOMBRE,
        })
      );
    }
  },

  SOCKET_editEstadoEmergencia(state, data) {
    const emergencia = state.emergencias.find((x) => x.ID_EMERGENCIA.equalsIgnoreCase(data.ID_EMERGENCIA));

    if (emergencia) {
      for (let i = 0; i < emergencia.ESTADOS.length; i++) {
        const estado = emergencia.ESTADOS[i];

        if (estado.ID_EMERGENCIA_ESTADO_TIPO.equalsIgnoreCase(data.ID_EMERGENCIA_ESTADO_TIPO)) {
          emergencia.ESTADOS[i].FECHA = data.FECHA;
          recordWrite(state.sync, 'estado', estado.ID_EMERGENCIA_ESTADO);

          if (data.CODIGO_TIPO_ESTADO === 0) {
            // Fecha inicio
            emergencia.FECHA_CREACION = data.FECHA;
            recordWrite(state.sync, 'emergencia', data.ID_EMERGENCIA);
          }

          this.notify(
            i18n.t('alert.emergencyStateUpdated', {
              nombre: emergencia.MUNICIPIO?.NOMBRE,
              estado: estado.EMERGENCIA_ESTADO_TIPO.EMERGENCIA_ESTADO,
            })
          );
          break;
        }
      }
    }
  },

  SOCKET_addRecursosEmergencia(state, data) {
    try {
      data.forEach((recursoSector) => {
        // Buscar emergencia con ese sector
        const idxEmergencia = state.emergencias.findIndex(
          (e) => Array.isArray(e.SECTORES) && e.SECTORES.some((s) => s.ID_SECTOR === recursoSector.ID_SECTOR)
        );

        if (idxEmergencia === -1) return;

        const emergencia = state.emergencias[idxEmergencia];

        // Buscar sector dentro de la emergencia
        const idxSector = emergencia.SECTORES.findIndex((s) => s.ID_SECTOR === recursoSector.ID_SECTOR);
        if (idxSector === -1) return;

        const sector = emergencia.SECTORES[idxSector];
        if (!Array.isArray(sector.RECURSOS)) {
          Vue.set(sector, 'RECURSOS', []);
        }

        const idxRecurso = sector.RECURSOS.findIndex((r) => r.ID_RECURSO_SECTOR === recursoSector.ID_RECURSO_SECTOR);

        if (idxRecurso !== -1) {
          // Recurso ya existe → actualizar estados
          const recursoExistente = sector.RECURSOS[idxRecurso];
          if (!Array.isArray(recursoExistente.RECURSO_SECTOR_ESTADO)) {
            Vue.set(recursoExistente, 'RECURSO_SECTOR_ESTADO', []);
          }

          recursoSector.RECURSO_SECTOR_ESTADO.forEach((estado) => {
            const idxEstado = recursoExistente.RECURSO_SECTOR_ESTADO.findIndex(
              (e) => e.ID_RECURSO_SECTOR_ESTADO === estado.ID_RECURSO_SECTOR_ESTADO
            );

            if (idxEstado !== -1) {
              Vue.set(recursoExistente.RECURSO_SECTOR_ESTADO, idxEstado, estado);
            } else {
              recursoExistente.RECURSO_SECTOR_ESTADO.push(estado);
            }
          });

          Vue.set(sector.RECURSOS, idxRecurso, recursoExistente);
        } else {
          // Nuevo recurso
          const nuevosRecursos = [...(sector.RECURSOS || []), { ...recursoSector, SECTOR: null }];
          Vue.set(sector, 'RECURSOS', nuevosRecursos);
        }

        // Refrescar la estructura para mantener reactividad
        Vue.set(emergencia.SECTORES, idxSector, sector);
        Vue.set(state.emergencias, idxEmergencia, emergencia);

        recordWrite(state.sync, 'recursoSector', recursoSector.ID_RECURSO_SECTOR);
      });
    } catch (error) {
      console.error(i18n.t('alert.error.addEmgRscSocket'), error);
    }
  },

  SOCKET_deleteEmergencia(state, data) {
    recordWrite(state.sync, 'emergencia', data, true);

    let notificar = false;
    const index = state.emergencias.findIndex((x) => x.ID_EMERGENCIA.equalsIgnoreCase(data));
    if (index !== -1) {
      state.emergencias.splice(index, 1);
      notificar = true;
    }
    if (notificar) {
      this.notify(i18n.t('alert.emergencyDelete'), i18n.t('alert.emergencyDeleted'));
    }
  },

  SOCKET_editRecursoEmergencia(state, data) {
    const indexEmergencia = state.emergencias.findIndex((emergencia) =>
      emergencia.SECTORES.some((sector) =>
        sector.RECURSOS.some((recurso) => recurso.ID_RECURSO_SECTOR === data.ID_RECURSO_SECTOR)
      )
    );

    if (indexEmergencia === -1) return;

    const emergencia = state.emergencias[indexEmergencia];

    const indexSector = emergencia.SECTORES.findIndex((sector) =>
      sector.RECURSOS.some((recurso) => recurso.ID_RECURSO_SECTOR === data.ID_RECURSO_SECTOR)
    );

    if (indexSector === -1) return;

    const sector = emergencia.SECTORES[indexSector];

    const indexRecursoSector = sector.RECURSOS.findIndex(
      (recurso) => recurso.ID_RECURSO_SECTOR === data.ID_RECURSO_SECTOR
    );

    if (indexRecursoSector === -1) return;

    const existing = sector.RECURSOS[indexRecursoSector];
    Vue.set(sector.RECURSOS, indexRecursoSector, { ...existing, ...data });

    recordWrite(state.sync, 'recursoSector', data.ID_RECURSO_SECTOR);
  },

  SOCKET_addSector(state, data) {
    const emergencia = state.emergencias.find((emergencia) => emergencia.ID_EMERGENCIA === data.ID_EMERGENCIA);
    if (emergencia) {
      emergencia.SECTORES.push(data);
      recordWrite(state.sync, 'sector', data.ID_SECTOR);
      this.notify(i18n.t('alert.sectorAdd'), i18n.t('alert.sectorAdded', { sector: data.SECTOR }));
    }
  },

  SOCKET_deleteSector(state, idSector) {
    recordWrite(state.sync, 'sector', idSector, true);

    const emergencia = state.emergencias.find((emergencia) =>
      emergencia.SECTORES.some((sector) => sector.ID_SECTOR === idSector)
    );

    const indexSector = emergencia.SECTORES.findIndex((sector) => sector.ID_SECTOR === idSector);

    if (indexSector > -1) {
      emergencia.SECTORES.splice(indexSector, 1);
      this.notify(i18n.t('alert.sectorDelete'), i18n.t('alert.sectorDeleted'));
    }
  },

  SOCKET_updateSector(state, data) {
    const emergencia = state.emergencias.find((e) => e.ID_EMERGENCIA === data.ID_EMERGENCIA);

    if (!emergencia) {
      console.warn(i18n.t('alert.emergencyNotFound'));
      return;
    }

    const indexSector = emergencia.SECTORES.findIndex((sector) => sector.ID_SECTOR === data.ID_SECTOR);

    if (indexSector === -1) {
      console.warn(i18n.t('alert.sectorNotFound'));
      return;
    }

    emergencia.SECTORES.splice(indexSector, 1, {
      ...emergencia.SECTORES[indexSector],
      ...data,
    });

    recordWrite(state.sync, 'sector', data.ID_SECTOR);

    this.notify(i18n.t('alert.sectorUpdate'), i18n.t('alert.sectorUpdated', { sector: data.SECTOR }));
  },

  SOCKET_updateRecursoSector(state, data) {
    const { idRecursoSector, newSectorId, oldSectorId } = data;

    const indexEmergencia = state.emergencias.findIndex((emergencia) =>
      emergencia.SECTORES.some((sector) =>
        sector.RECURSOS.some((recursoSector) => recursoSector.ID_RECURSO_SECTOR === idRecursoSector)
      )
    );

    if (indexEmergencia === -1) return;

    const emergencia = state.emergencias[indexEmergencia];

    const oldSectorIndex = emergencia.SECTORES.findIndex((sector) => sector.ID_SECTOR === oldSectorId);
    if (oldSectorIndex === -1) return;

    const oldSector = emergencia.SECTORES[oldSectorIndex];

    const recursoIndex = oldSector.RECURSOS.findIndex((r) => r.ID_RECURSO_SECTOR === idRecursoSector);
    if (recursoIndex === -1) return;

    const [recurso] = oldSector.RECURSOS.splice(recursoIndex, 1);

    const newSectorIndex = emergencia.SECTORES.findIndex((sector) => sector.ID_SECTOR === newSectorId);
    if (newSectorIndex === -1) return;

    const newSector = emergencia.SECTORES[newSectorIndex];
    recurso.ID_SECTOR = newSectorId;

    newSector.RECURSOS.push(recurso);

    Vue.set(emergencia.SECTORES, oldSectorIndex, oldSector);
    Vue.set(emergencia.SECTORES, newSectorIndex, newSector);

    recordWrite(state.sync, 'recursoSector', idRecursoSector);
  },

  SOCKET_deleteRecursoEmergencia(state, data) {
    recordWrite(state.sync, 'recursoSector', data, true);

    const indexEmergencia = state.emergencias.findIndex((emergencia) =>
      emergencia.SECTORES.some((sector) =>
        sector.RECURSOS.some((recursoSector) => recursoSector.ID_RECURSO_SECTOR === data)
      )
    );

    if (indexEmergencia !== -1) {
      const emergencia = state.emergencias[indexEmergencia];

      const indexSector = emergencia.SECTORES.findIndex((sector) =>
        sector.RECURSOS.some((recursoSector) => recursoSector.ID_RECURSO_SECTOR === data)
      );

      if (indexSector !== -1) {
        const sector = emergencia.SECTORES[indexSector];

        const indexRecursoSector = sector.RECURSOS.findIndex(
          (recursoSector) => recursoSector.ID_RECURSO_SECTOR === data
        );

        if (indexRecursoSector !== -1) {
          sector.RECURSOS.splice(indexRecursoSector, 1);
        }
      }
    }
  },

  SOCKET_addPlanOperaciones(state, data) {
    const position = state.emergencias.findIndex((x) => x.ID_EMERGENCIA.equalsIgnoreCase(data.ID_EMERGENCIA));

    if (position !== -1) {
      const discardedGraficos = data.GRAFICOS ? normalizeGraficos(data.GRAFICOS, data.ID_PLAN_OPERACIONES) : 0;

      state.emergencias[position].PLANES_OPERACIONES.unshift(data);
      recordWrite(state.sync, 'plan', data.ID_PLAN_OPERACIONES);
      this.notify(
        i18n.t('alert.newOperationsPlan'),
        i18n.t('alert.newOperationsPlanCreated', { municipio: state.emergencias[position].MUNICIPIO?.NOMBRE })
      );
      if (discardedGraficos > 0 && !state.corruptGraficosNotified) {
        state.corruptGraficosNotified = true;
        this.notify(
          i18n.t('alert.info'),
          i18n.t('alert.operationsPlanGraphicsDiscarded', { count: discardedGraficos }),
          'warn'
        );
      }
    }
  },

  SOCKET_deletePlanOperaciones(state, data) {
    recordWrite(state.sync, 'plan', data.ID_PLAN_OPERACIONES, true);

    const emergenciaIndex = state.emergencias.findIndex((x) => x.ID_EMERGENCIA.equalsIgnoreCase(data.ID_EMERGENCIA));

    if (emergenciaIndex !== -1) {
      const planIndex = state.emergencias[emergenciaIndex].PLANES_OPERACIONES.findIndex((x) =>
        x.ID_PLAN_OPERACIONES.equalsIgnoreCase(data.ID_PLAN_OPERACIONES)
      );

      if (planIndex !== -1) {
        state.emergencias[emergenciaIndex].PLANES_OPERACIONES.splice(planIndex, 1);
        this.notify(
          i18n.t('alert.operationsPlanDelete'),
          i18n.t('alert.operationsPlanModified', { municipio: state.emergencias[emergenciaIndex].MUNICIPIO?.NOMBRE })
        );
      }
    }
  },

  SOCKET_editPlanOperaciones(state, data) {
    const emergenciaIndex = state.emergencias.findIndex((x) => x.ID_EMERGENCIA.equalsIgnoreCase(data.ID_EMERGENCIA));

    if (emergenciaIndex !== -1) {
      const planIndex = state.emergencias[emergenciaIndex].PLANES_OPERACIONES.findIndex((x) =>
        x.ID_PLAN_OPERACIONES.equalsIgnoreCase(data.ID_PLAN_OPERACIONES)
      );

      if (planIndex !== -1) {
        const discardedGraficos = data.GRAFICOS ? normalizeGraficos(data.GRAFICOS, data.ID_PLAN_OPERACIONES) : 0;

        state.emergencias[emergenciaIndex].PLANES_OPERACIONES[planIndex] = data;
        Vue.set(state.emergencias, emergenciaIndex, state.emergencias[emergenciaIndex]);
        recordWrite(state.sync, 'plan', data.ID_PLAN_OPERACIONES);
        this.notify(
          i18n.t('alert.operationsPlanModify'),
          i18n.t('alert.operationsPlanModified', { municipio: state.emergencias[emergenciaIndex].MUNICIPIO?.NOMBRE })
        );
        if (discardedGraficos > 0 && !state.corruptGraficosNotified) {
          state.corruptGraficosNotified = true;
          this.notify(
            i18n.t('alert.info'),
            i18n.t('alert.operationsPlanGraphicsDiscarded', { count: discardedGraficos }),
            'warn'
          );
        }
      }
    }
  },

  SOCKET_addPerimetro(state, data) {
    const position = state.emergencias.findIndex((x) => x.ID_EMERGENCIA.equalsIgnoreCase(data.ID_EMERGENCIA));

    if (position !== -1) {
      state.emergencias[position].PERIMETRO.unshift(data);
      recordWrite(state.sync, 'perimetro', data.ID_PERIMETRO);
      this.notify(
        i18n.t('alert.newPerimeter'),
        i18n.t('alert.perimeterCreated', { municipio: state.emergencias[position].MUNICIPIO?.NOMBRE })
      );
    }
  },

  SOCKET_editPerimetro(state, data) {
    const emergenciaIndex = state.emergencias.findIndex((x) => x.ID_EMERGENCIA.equalsIgnoreCase(data.ID_EMERGENCIA));

    if (emergenciaIndex !== -1) {
      const perimetroIndex = state.emergencias[emergenciaIndex].PERIMETRO.findIndex((x) =>
        x.ID_PERIMETRO.equalsIgnoreCase(data.ID_PERIMETRO)
      );

      if (perimetroIndex !== -1) {
        state.emergencias[emergenciaIndex].PERIMETRO[perimetroIndex].GRAFICOS_PERIMETRO = data.GRAFICOS_PERIMETRO; // Hay que acceder a los datos de los gráficos
        state.emergencias[emergenciaIndex].PERIMETRO[perimetroIndex].measure = data.measure;
        Vue.set(state.emergencias, emergenciaIndex, state.emergencias[emergenciaIndex]);
        recordWrite(state.sync, 'perimetro', data.ID_PERIMETRO);
        this.notify(
          i18n.t('alert.perimeterModify'),
          i18n.t('alert.perimeterModified', { municipio: state.emergencias[emergenciaIndex].MUNICIPIO?.NOMBRE })
        );
      }
    }
  },

  SOCKET_deletePerimetro(state, data) {
    recordWrite(state.sync, 'perimetro', data.ID_PERIMETRO, true);

    const emergenciaIndex = state.emergencias.findIndex((x) => x.ID_EMERGENCIA.equalsIgnoreCase(data.ID_EMERGENCIA));

    if (emergenciaIndex !== -1) {
      const perimetroIndex = state.emergencias[emergenciaIndex].PERIMETRO.findIndex((x) =>
        x.ID_PERIMETRO.equalsIgnoreCase(data.ID_PERIMETRO)
      );

      if (perimetroIndex !== -1) {
        state.emergencias[emergenciaIndex].PERIMETRO.splice(perimetroIndex, 1);
        this.notify(
          i18n.t('alert.perimeterDelete'),
          i18n.t('alert.perimeterDeleted', { municipio: state.emergencias[emergenciaIndex].MUNICIPIO?.NOMBRE })
        );
      }
    }
  },

  SOCKET_addDirectorExtincion(state, data) {
    const emergenciaIndex = state.emergencias.findIndex((x) => x.ID_EMERGENCIA.equalsIgnoreCase(data.ID_EMERGENCIA));
    if (emergenciaIndex !== -1) {
      state.emergencias[emergenciaIndex].DIRECTOR_EXTINCION.push(data);
      recordWrite(state.sync, 'director', data.ID_DIRECTOR_EXTINCION);
      this.notify(
        i18n.t('alert.extinctionManager'),
        i18n.t('alert.extinctionManagerAdded', { municipio: state.emergencias[emergenciaIndex].MUNICIPIO?.NOMBRE })
      );
    }
  },

  SOCKET_editDirectorExtincion(state, data) {
    const emergenciaIndex = state.emergencias.findIndex((x) => x.ID_EMERGENCIA.equalsIgnoreCase(data.ID_EMERGENCIA));
    if (emergenciaIndex !== -1) {
      const directorIndex = state.emergencias[emergenciaIndex].DIRECTOR_EXTINCION.findIndex((x) =>
        x.ID_DIRECTOR_EXTINCION.equalsIgnoreCase(data.ID_DIRECTOR_EXTINCION)
      );
      if (directorIndex !== -1) {
        state.emergencias[emergenciaIndex].DIRECTOR_EXTINCION[directorIndex] = data;
        Vue.set(state.emergencias, emergenciaIndex, state.emergencias[emergenciaIndex]);
        recordWrite(state.sync, 'director', data.ID_DIRECTOR_EXTINCION);
        this.notify(
          i18n.t('alert.extinctionManager'),
          i18n.t('alert.extinctionManagerUpdated', { municipio: state.emergencias[emergenciaIndex].MUNICIPIO?.NOMBRE })
        );
      }
    }
  },

  SOCKET_editDirectorPrincipal(state, data) {
    const emergenciaIndex = state.emergencias.findIndex((x) => x.ID_EMERGENCIA.equalsIgnoreCase(data.ID_EMERGENCIA));
    if (emergenciaIndex !== -1) {
      const directorIndex = state.emergencias[emergenciaIndex].DIRECTOR_EXTINCION.findIndex((x) =>
        x.ID_DIRECTOR_EXTINCION.equalsIgnoreCase(data.ID_DIRECTOR_EXTINCION)
      );
      if (directorIndex !== -1) {
        state.emergencias[emergenciaIndex].DIRECTOR_EXTINCION.forEach((director) => {
          director.PRINCIPAL = false;
          recordWrite(state.sync, 'director', director.ID_DIRECTOR_EXTINCION);
        });
        state.emergencias[emergenciaIndex].DIRECTOR_EXTINCION[directorIndex].PRINCIPAL = data.PRINCIPAL;
        Vue.set(state.emergencias, emergenciaIndex, state.emergencias[emergenciaIndex]);
        this.notify(
          i18n.t('alert.extinctionManager'),
          i18n.t('alert.extinctionLeadManagerUpdated', {
            municipio: state.emergencias[emergenciaIndex].MUNICIPIO?.NOMBRE,
          })
        );
      }
    }
  },

  SOCKET_addGuardiaEnRecursoSector(state, data) {
    // Filtrar emergencias visibles
    const { idsRecursoSector, medioPersonalUpdated } = data;
    const emergencias = state.emergencias.filter((e) => !e.OCULTO);

    for (const emergencia of emergencias) {
      for (const sector of emergencia.SECTORES) {
        for (const recursoSector of sector.RECURSOS) {
          if (idsRecursoSector.includes(recursoSector.ID_RECURSO_SECTOR)) {
            Vue.set(recursoSector, 'MEDIO_PERSONAL', medioPersonalUpdated);
            Vue.set(recursoSector, 'ID_MEDIO_PERSONAL', medioPersonalUpdated?.ID_MEDIO_PERSONAL ?? null);
            recordWrite(state.sync, 'recursoSectorGuardia', recursoSector.ID_RECURSO_SECTOR);
          }
        }
      }
    }
  },

  SOCKET_deleteGuardiaEnRecursoSector(state, idMedioPersonal) {
    // Filtrar emergencias visibles
    const emergencias = state.emergencias.filter((e) => !e.OCULTO);

    for (const emergencia of emergencias) {
      for (const sector of emergencia.SECTORES) {
        for (const recursoSector of sector.RECURSOS) {
          if (recursoSector.MEDIO_PERSONAL?.ID_MEDIO_PERSONAL === idMedioPersonal) {
            // Eliminar el id
            Vue.set(recursoSector, 'ID_MEDIO_PERSONAL', null);
            Vue.set(recursoSector, 'MEDIO_PERSONAL', null);
            recordWrite(state.sync, 'recursoSectorGuardia', recursoSector.ID_RECURSO_SECTOR);
          }
        }
      }
    }
  },
};

const actions = {
  async fetchEmergencias({ commit, dispatch, state }) {
    try {
      commit('BEGIN_SYNC_REQUEST');
      const request = createSyncRequest(state.sync.seq);

      const response = await api.emergencia.getEmergencias();

      for (let i = 0; i < response.data.length; i++) {
        // TODO: Revisar, para almacenar en BBDD
        for (let j = 0; j < response.data[i].PERIMETRO.length; j++) {
          response.data[i].PERIMETRO[j].measure = await ArcGIS.measurePerimetros(
            response.data[i].PERIMETRO[j].GRAFICOS_PERIMETRO
          );
        }
      }
      commit('MERGE_EMERGENCIAS', { rows: response.data, request });

      await dispatch('medioPersonal/ensureGuardiasForEmergencias', state.emergencias, { root: true });
    } catch (err) {
      console.error(err);
      throw err;
    }
  },

  async fetchEmergenciasAll({ commit, state }) {
    try {
      commit('BEGIN_SYNC_REQUEST');
      const request = createSyncRequest(state.sync.seq);

      const response = await api.emergencia.getEmergenciasAll();
      commit('MERGE_EMERGENCIAS', { rows: response.data, request });
    } catch (err) {
      console.error(err);
      throw err;
    }
  },

  async fetchEmergenciaByID({ commit }, id) {
    try {
      const response = await api.emergencia.getEmergenciaByID(id);
      commit('SET_EMERGENCIA_BY_ID', response.data);
    } catch (err) {
      console.error(err);
      throw err;
    }
  },

  async fetchIncidenteLog({ commit }, idEmergencia) {
    try {
      const response = await api.emergencia.getIncidenteLog(idEmergencia);
      return response.data;
    } catch (err) {
      console.error(err);
      throw err;
    }
  },

  async vaciarEmergenciasAll({ commit }) {
    commit('CLEAR_EMERGENCIAS');
  },

  async fetchEmergenciaNivel({ commit }) {
    try {
      const response = await api.emergencia.getTiposEmergenciaNivel();
      commit('SET_TIPOS_EMERGENCIA_NIVEL', response.data);
    } catch (err) {
      console.error(err);
    }
  },

  async fetchEmergenciasTipo({ commit }) {
    try {
      const response = await api.emergencia.getEmergenciasTipo();
      if (response.status === 200) {
        commit('SET_EMERGENCIAS_TIPO', response.data);
      }
    } catch (err) {
      console.error(i18n.t('alert.error.emergencyTypeRetrieval'), err);
      throw err;
    }
  },

  async fetchEmergenciaEstadoTipo({ commit }) {
    try {
      const response = await api.emergencia.getEmergenciaEstadoTipo();
      commit('SET_EMERGENCIAS_ESTADO_TIPO', response.data);
    } catch (err) {
      console.error(i18n.t('alert.error.emergencyTypeRetrieval'), err);
      throw err;
    }
  },

  async crearEmergencia({ commit }, nuevaEmergencia) {
    try {
      await api.emergencia.crearEmergencia(nuevaEmergencia);
    } catch (err) {
      console.error(err);
      throw err;
    }
  },

  async updateEmergencia({ commit }, emergenciaActualizada) {
    try {
      const response = await api.emergencia.updateEmergencia(emergenciaActualizada);
      return response.data;
    } catch (error) {
      console.error(i18n.t('alert.error.emergencyUpdate'), error);
      throw error;
    }
  },

  async cambiarOcultoEmergencia({ dispatch }, data) {
    try {
      await api.emergencia.cambiarOcultoEmergencia(data);
    } catch (err) {
      console.error(err);
      throw err;
    }
  },

  async addEstadoEmergencia({ commit }, data) {
    try {
      await api.emergencia.addEstadoEmergencia(data);
    } catch (err) {
      console.error(err);
      throw err;
    }
  },

  async editEstadoEmergencia({ commit }, data) {
    try {
      await api.emergencia.updateEstadoEmergencia(data);
    } catch (err) {
      console.error(i18n.t('alert.error.emergencyStateEdit'), err);
      throw err;
    }
  },

  async deleteEstadoEmergencia({ commit }, data) {
    try {
      await api.emergencia.deleteEstadoEmergencia(data);
    } catch (err) {
      console.error(err);
      throw err;
    }
  },

  async deleteEmergencia({ commit }, idEmergencia) {
    try {
      await api.emergencia.deleteEmergencia(idEmergencia);
    } catch (err) {
      console.error(err);
      throw err;
    }
  },

  // SECTORES
  async addSector({ commit }, data) {
    try {
      await api.emergencia.addSector(data);
    } catch (err) {
      console.error(err);
      throw err;
    }
  },

  async updateSector({ commit }, data) {
    try {
      await api.emergencia.updateSector(data);
    } catch (err) {
      console.error(err);
      throw err;
    }
  },

  async deleteSector({ commit }, idSector) {
    try {
      await api.emergencia.deleteSector(idSector);
    } catch (err) {
      console.error(err);
      throw err;
    }
  },

  async asignarRecursos({ commit }, data) {
    try {
      await api.emergencia.addRecursosEmergencia(data.RECURSOS);
    } catch (error) {
      console.error(i18n.t('alert.error.rscAllocation'), error);
      throw error;
    }
  },

  async editRecursoEmergencia({ commit }, data) {
    try {
      await api.emergencia.editRecursoEmergencia(data);
    } catch (err) {
      console.error(i18n.t('alert.error.emergencyRscEdit'), err);
      throw err;
    }
  },

  async updateRecursoSector({ commit }, data) {
    try {
      await api.emergencia.updateRecursoSector(data);
    } catch (err) {
      console.error(i18n.t('alert.error.emergencySectorEdit'), err);
      throw err;
    }
  },

  async deleteRecursoEmergencia({ commit }, idRecursoSector) {
    try {
      const response = await api.emergencia.deleteRecursoEmergencia(idRecursoSector);

      if (response.status === 200) {
        this.notify(i18n.t('alert.info'), i18n.t('alert.performanceDelete'));
      }
    } catch (err) {
      console.error(i18n.t('alert.error.emergencyRscDelete'), err);
      throw err;
    }
  },

  // TODO: CAMBIAR A MUTATION GRAN PARTE DE LA LÓGICA
  pasarEstadoDeRecurso({ state, rootGetters, dispatch }, { idEmergencia, fechaEstado, nombreEstadoEmergencia }) {
    const emergencia = state.emergencias.find((e) => e.ID_EMERGENCIA === idEmergencia);

    const nombresEstadosEnIncendio = [
      RECURSO_SECTOR_ESTADO_TIPO.EN_INCENDIO.NOMBRE,
      RECURSO_SECTOR_ESTADO_TIPO.EN_INCIDENTE.NOMBRE,
    ];

    const nombresEstadosFinales = [
      RECURSO_SECTOR_ESTADO_TIPO.LIQUIDACION.NOMBRE,
      RECURSO_SECTOR_ESTADO_TIPO.RETIRADA.NOMBRE,
      RECURSO_SECTOR_ESTADO_TIPO.REGRESO.NOMBRE,
      RECURSO_SECTOR_ESTADO_TIPO.DESASIGNADO.NOMBRE,
    ];

    const recursosValidos = emergencia.SECTORES.flatMap((sector) => sector.RECURSOS).filter(
      (recurso) =>
        recurso.RECURSO_SECTOR_ESTADO.some((estado) =>
          nombresEstadosEnIncendio.includes(estado.RECURSO_SECTOR_ESTADO_TIPO.NOMBRE)
        ) &&
        !recurso.RECURSO_SECTOR_ESTADO.some((estado) =>
          nombresEstadosFinales.includes(estado.RECURSO_SECTOR_ESTADO_TIPO.NOMBRE)
        )
    );

    if (recursosValidos.length === 0) return;

    const nuevoEstadoEmergencia = rootGetters['recurso/getRecursoEstadoTipoByEmergenciaTipo'](
      emergencia.ID_EMERGENCIA_TIPO
    ).find((et) => et.NOMBRE === nombreEstadoEmergencia);

    recursosValidos.forEach(async (recurso) => {
      const nuevoEstado = {
        ID_RECURSO_SECTOR: recurso.ID_RECURSO_SECTOR,
        ID_RECURSO_SECTOR_ESTADO_TIPO: nuevoEstadoEmergencia.ID_RECURSO_SECTOR_ESTADO_TIPO,
        FECHA: fechaEstado.add(1, 'second'),
        RECURSO_SECTOR_ESTADO_TIPO: { ...nuevoEstadoEmergencia },
      };
      try {
        await dispatch('editRecursoEmergencia', {
          ...recurso,
          RECURSO_SECTOR_ESTADO: [...recurso.RECURSO_SECTOR_ESTADO, nuevoEstado],
        });
      } catch (error) {
        console.error(i18n.t('alert.error.rscState'), error);
        throw error;
      }
    });
  },

  // #region PLAN OPERACIONES
  async addPlanOperaciones(_context, data) {
    try {
      await api.emergencia.addPlanOperaciones(data);
    } catch (err) {
      console.error(i18n.t('alert.error.addOperationsPlan'), err);
      throw err;
    }
  },

  async deletePlanOperaciones(_context, data) {
    try {
      await api.emergencia.deletePlanOperaciones(data);
    } catch (err) {
      console.error(err);
      throw err;
    }
  },

  async editPlanOperaciones(_context, data) {
    try {
      await api.emergencia.editPlanOperaciones(data);
    } catch (err) {
      console.error(err);
      throw err;
    }
  },
  // #endregion

  // #region PERIMETRO
  // TODO: Mover a store de incendio
  async addPerimetro({ commit }, data) {
    try {
      const response = await api.emergencia.addPerimetro(data);

      if (response.status === 200) {
        commit('SOCKET_addPerimetro', data);
      }
    } catch (err) {
      console.error(i18n.t('alert.error.addPerimeter'), err);
      throw err;
    }
  },

  async editPerimetro({ commit }, data) {
    try {
      const response = await api.emergencia.editPerimetro(data);

      if (response.status === 200) {
        commit('SOCKET_editPerimetro', data);
      }
    } catch (err) {
      console.error(i18n.t('alert.error.editPerimeter'), err);
      throw err;
    }
  },

  async deletePerimetro({ commit }, data) {
    try {
      const response = await api.emergencia.deletePerimetro(data);

      if (response.status === 200) {
        commit('SOCKET_deletePerimetro', data);
      }
    } catch (err) {
      console.error(i18n.t('alert.error.deletePerimeter'), err);
      throw err;
    }
  },
  // #endregion

  async addDirectorExtincion({ commit }, data) {
    try {
      await api.emergencia.addDirectorExtincion(data);
    } catch (err) {
      console.error(i18n.t('alert.error.addExtinctionManager'), err);
      throw err;
    }
  },

  async updateDirectorExtincion({ commit }, data) {
    try {
      await api.emergencia.editDirectorExtincion(data);
    } catch (err) {
      console.error(i18n.t('alert.error.updateExtinctionManager'), err);
      throw err;
    }
  },

  async updateDirectorPrincipal({ commit }, data) {
    try {
      await api.emergencia.editDirectorPrincipal(data);
    } catch (err) {
      console.error(i18n.t('alert.error.updateExtinctionLeadManager'), err);
      throw err;
    }
  },

  async addNivelEmergencia({ commit }, data) {
    try {
      await api.emergencia.addNivelEmergencia(data);
    } catch (err) {
      console.error(i18n.t('alert.error.addWildfireLevel'), err);
      throw err;
    }
  },

  async editNivelEmergencia({ commit }, data) {
    try {
      await api.emergencia.editNivelEmergencia(data);
    } catch (err) {
      console.error(i18n.t('alert.error.editWildfireLevel'), err);
      throw err;
    }
  },
};

const module = {
  namespaced: true,
  state: initialState,
  getters,
  mutations,
  actions,
};

export default module;
